import { useState, useCallback } from "react";
import { CategoryTableHeader } from "./CategoryTableHeader";
import { CategoryTableRow } from "./CategoryTableRow";
import type { Category } from "@/services/api/categories";
import type { SortField, SortDirection } from "./categoryUtils";

interface CategoryTableProps {
  categories: Category[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onViewAttributes: (category: Category) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

export function CategoryTable({
  categories,
  sortField,
  sortDirection,
  onSort,
  onEdit,
  onDelete,
  onViewAttributes,
  onReorder,
}: CategoryTableProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const isDraggable = sortField === "order" && sortDirection === "asc";

  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === index) {
        setDragOverIndex(null);
        return;
      }
      setDragOverIndex(index);
    },
    [draggedIndex]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === dropIndex) {
        setDraggedIndex(null);
        setDragOverIndex(null);
        return;
      }
      onReorder(draggedIndex, dropIndex);
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    [draggedIndex, onReorder]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  if (categories.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-ink-500">
        No categories found. Create your first category to get started.
      </div>
    );
  }

  return (
    <div className="max-h-[70vh] overflow-auto pb-8">
      <table className="w-full">
        <CategoryTableHeader
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={onSort}
          isDraggable={isDraggable}
        />
        <tbody className="divide-y divide-grid-200">
          {categories.map((category, index) => (
            <CategoryTableRow
              key={String(category.id)}
              category={category}
              index={index}
              isDraggable={isDraggable}
              isDragging={draggedIndex === index}
              isDragOver={dragOverIndex === index && draggedIndex !== index}
              onEdit={onEdit}
              onDelete={onDelete}
              onViewAttributes={onViewAttributes}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
